import { codeScancodeMap, KeyboardEventCode } from './keyboardScancode';

export * from './keyboardScancode';

export const mapKeyEventToWinScanCode = (e: KeyboardEvent): number =>
  mapCodeToWinScanCode(e.code as KeyboardEventCode, () => {
    console.warn('Unhandled code', e);
    return e.keyCode;
  });

export const mapCodeToWinScanCode = (
  code: KeyboardEventCode,
  fallback: () => number,
): number => {
  if (codeScancodeMap.has(code)) {
    return codeScancodeMap.get(code);
  }
  return fallback();
};

export type LiveKeyPayload =
  | { type: 'scanCode'; scanCode: number; vkCode: number }
  | {
      type: 'character';
      character: string;
      scanCode: number;
      vkCode: number;
    };

export const mapKeyEventToLiveKeyPayload = (
  e: KeyboardEvent,
): LiveKeyPayload => {
  if (
    !nonCharacterKeySet.has(e.code as KeyboardEventCode) &&
    codeScancodeMap.has(e.code as KeyboardEventCode)
  ) {
    return {
      type: 'scanCode',
      scanCode: mapKeyEventToWinScanCode(e),
      vkCode: mapKeyEventToWinCode(e),
    };
  }

  return {
    type: 'character',
    character: e.key,
    scanCode: mapKeyEventToWinScanCode(e),
    vkCode: mapKeyEventToWinCode(e),
  };
};

export const mapKeyEventToWinCode = (e: KeyboardEvent): number => {
  const code = (e.code as any) as KeyboardEventCode;

  // For the backwards-compability and in case we missed some code, let's use deprecated keyCode here
  return mapKeyboardEventCodeToWindowsCode(code, () => {
    console.warn('Unhandled code', e);
    return e.keyCode;
  });
};

// https://docs.microsoft.com/en-us/windows/desktop/inputdev/virtual-key-codes
export const mapKeyboardEventCodeToWindowsCode = (
  code: KeyboardEventCode,
  fallback: () => number,
) => {
  switch (code) {
    case 'AltLeft':
      return parseInt('A4', 16);
    case 'AltRight':
      return parseInt('A5', 16);
    case 'ArrowDown':
      return parseInt('28', 16);
    case 'ArrowLeft':
      return parseInt('25', 16);
    case 'ArrowRight':
      return parseInt('27', 16);
    case 'ArrowUp':
      return parseInt('26', 16);
    case 'AudioVolumeDown':
    case 'VolumeDown':
      return parseInt('AE', 16);
    case 'AudioVolumeUp':
    case 'VolumeUp':
      return parseInt('AF', 16);
    case 'AudioVolumeMute':
      return parseInt('AD', 16);
    case 'Backquote':
      return parseInt('C0', 16);
    case 'Backslash':
      return parseInt('DC', 16);
    case 'Backspace':
      return parseInt('08', 16);
    case 'BracketLeft':
      return parseInt('DB', 16);
    case 'BracketRight':
      return parseInt('DD', 16);
    case 'BrowserHome':
      return parseInt('AC', 16);
    case 'BrowserSearch':
      return parseInt('AA', 16);
    case 'BrowserFavorites':
      return parseInt('AB', 16);
    case 'BrowserRefresh':
      return parseInt('A8', 16);
    case 'BrowserStop':
      return parseInt('A9', 16);
    case 'BrowserForward':
      return parseInt('A7', 16);
    case 'BrowserBack':
      return parseInt('A6', 16);
    case 'CapsLock':
      return parseInt('14', 16);
    case 'Comma':
      return parseInt('BC', 16);
    case 'ContextMenu':
      return parseInt('5D', 16);
    case 'ControlLeft':
      return parseInt('A2', 16);
    case 'ControlRight':
      return parseInt('A3', 16);
    case 'Convert':
      return parseInt('1C', 16);
    case 'NonConvert':
      return parseInt('1D', 16);
    case 'Delete':
      return parseInt('2E', 16);
    case 'Digit0':
      return parseInt('30', 16);
    case 'Digit1':
      return parseInt('31', 16);
    case 'Digit2':
      return parseInt('32', 16);
    case 'Digit3':
      return parseInt('33', 16);
    case 'Digit4':
      return parseInt('34', 16);
    case 'Digit5':
      return parseInt('35', 16);
    case 'Digit6':
      return parseInt('36', 16);
    case 'Digit7':
      return parseInt('37', 16);
    case 'Digit8':
      return parseInt('38', 16);
    case 'Digit9':
      return parseInt('39', 16);
    case 'End':
      return parseInt('23', 16);
    case 'Enter':
    case 'NumpadEnter':
    case 'NumpadEqual':
      return parseInt('0D', 16);
    case 'Equal':
      return parseInt('BB', 16);
    case 'Escape':
      return parseInt('1B', 16);
    case 'F1':
      return parseInt('70', 16);
    case 'F2':
      return parseInt('71', 16);
    case 'F3':
      return parseInt('72', 16);
    case 'F4':
      return parseInt('73', 16);
    case 'F5':
      return parseInt('74', 16);
    case 'F6':
      return parseInt('75', 16);
    case 'F7':
      return parseInt('76', 16);
    case 'F8':
      return parseInt('77', 16);
    case 'F9':
      return parseInt('78', 16);
    case 'F10':
      return parseInt('79', 16);
    case 'F11':
      return parseInt('7A', 16);
    case 'F12':
      return parseInt('7B', 16);
    case 'F13':
      return parseInt('7C', 16);
    case 'F14':
      return parseInt('7D', 16);
    case 'F15':
      return parseInt('7E', 16);
    case 'F16':
      return parseInt('7F', 16);
    case 'F17':
      return parseInt('80', 16);
    case 'F18':
      return parseInt('81', 16);
    case 'F19':
      return parseInt('82', 16);
    case 'F20':
      return parseInt('83', 16);
    case 'F21':
      return parseInt('84', 16);
    case 'F22':
      return parseInt('85', 16);
    case 'F23':
      return parseInt('86', 16);
    case 'F24':
      return parseInt('87', 16);
    case 'Help':
      return parseInt('2F', 16);
    case 'Home':
      return parseInt('24', 16);
    case 'Insert':
      return parseInt('2D', 16);
    case 'IntlBackslash':
      return parseInt('E2', 16);
    case 'IntlYen':
      return fallback();
    case 'KanaMode':
      return parseInt('15', 16);
    case 'KeyA':
      return parseInt('41', 16);
    case 'KeyB':
      return parseInt('42', 16);
    case 'KeyC':
      return parseInt('43', 16);
    case 'KeyD':
      return parseInt('44', 16);
    case 'KeyE':
      return parseInt('45', 16);
    case 'KeyF':
      return parseInt('46', 16);
    case 'KeyG':
      return parseInt('47', 16);
    case 'KeyH':
      return parseInt('48', 16);
    case 'KeyI':
      return parseInt('49', 16);
    case 'KeyJ':
      return parseInt('4A', 16);
    case 'KeyK':
      return parseInt('4B', 16);
    case 'KeyL':
      return parseInt('4C', 16);
    case 'KeyM':
      return parseInt('4D', 16);
    case 'KeyN':
      return parseInt('4E', 16);
    case 'KeyO':
      return parseInt('4F', 16);
    case 'KeyP':
      return parseInt('50', 16);
    case 'KeyQ':
      return parseInt('51', 16);
    case 'KeyR':
      return parseInt('52', 16);
    case 'KeyS':
      return parseInt('53', 16);
    case 'KeyT':
      return parseInt('54', 16);
    case 'KeyU':
      return parseInt('55', 16);
    case 'KeyV':
      return parseInt('56', 16);
    case 'KeyW':
      return parseInt('57', 16);
    case 'KeyX':
      return parseInt('58', 16);
    case 'KeyY':
      return parseInt('59', 16);
    case 'KeyZ':
      return parseInt('5A', 16);
    case 'LaunchApp1':
      return parseInt('B6', 16);
    case 'LaunchApp2':
      return parseInt('B7', 16);
    case 'LaunchMail':
      return parseInt('B4', 16);
    case 'LaunchMediaPlayer':
    case 'MediaSelect':
      return parseInt('B5', 16);
    case 'MediaPlayPause':
      return parseInt('B3', 16);
    case 'MediaStop':
      return parseInt('B2', 16);
    case 'MediaTrackNext':
      return parseInt('B0', 16);
    case 'MediaTrackPrevious':
      return parseInt('B1', 16);
    case 'MetaLeft':
    case 'OSLeft':
      return parseInt('5B', 16);
    case 'MetaRight':
    case 'OSRight':
      return parseInt('5C', 16);
    case 'Minus':
      return parseInt('BD', 16);
    case 'NumLock':
      return parseInt('90', 16);
    case 'Numpad0':
      return parseInt('60', 16);
    case 'Numpad1':
      return parseInt('61', 16);
    case 'Numpad2':
      return parseInt('62', 16);
    case 'Numpad3':
      return parseInt('63', 16);
    case 'Numpad4':
      return parseInt('64', 16);
    case 'Numpad5':
      return parseInt('65', 16);
    case 'Numpad6':
      return parseInt('66', 16);
    case 'Numpad7':
      return parseInt('67', 16);
    case 'Numpad8':
      return parseInt('68', 16);
    case 'Numpad9':
      return parseInt('69', 16);
    case 'NumpadAdd':
      return parseInt('6B', 16);
    case 'NumpadComma':
      return parseInt('6C', 16);
    case 'NumpadDecimal':
      return parseInt('6E', 16);
    case 'NumpadDivide':
      return parseInt('6F', 16);
    case 'NumpadMultiply':
      return parseInt('6A', 16);
    case 'NumpadSubtract':
      return parseInt('6D', 16);
    case 'PageDown':
      return parseInt('22', 16);
    case 'PageUp':
      return parseInt('21', 16);
    case 'Pause':
      return parseInt('13', 16);
    case 'Period':
      return parseInt('BE', 16);
    case 'PrintScreen':
      return parseInt('2C', 16);
    case 'Quote':
      return parseInt('DE', 16);
    case 'ScrollLock':
      return parseInt('91', 16);
    case 'Semicolon':
      return parseInt('BA', 16);
    case 'ShiftLeft':
      return parseInt('A0', 16);
    case 'ShiftRight':
      return parseInt('A1', 16);
    case 'Slash':
      return parseInt('BF', 16);
    case 'Space':
      return parseInt('20', 16);
    case 'Tab':
      return parseInt('09', 16);
    case 'Lang1':
    case 'Lang2':
    case 'Power':
    case 'IntlRo':
    case 'Unidentified':
    case '':
      console.warn('Unidentified code', code);
      return fallback();
  }

  const _exhaustiveCheck: never = code;

  return fallback();
};

// prettier-ignore
export const nonCharacterKeySet = new Set<KeyboardEventCode>([
  'Backquote' ,

  'Digit1' , 'Digit2' , 'Digit3' , 'Digit4' , 'Digit5' , 'Digit6' , 'Digit7' , 'Digit8' , 'Digit9' , 'Digit0'    , 'Minus'       , 'Equal'        ,
  'KeyQ'   , 'KeyW'   , 'KeyE'   , 'KeyR'   , 'KeyT'   , 'KeyY'   , 'KeyU'   , 'KeyI'   , 'KeyO'   , 'KeyP'      , 'BracketLeft' , 'BracketRight' , 'Backslash' ,
  'KeyA'   , 'KeyS'   , 'KeyD'   , 'KeyF'   , 'KeyG'   , 'KeyH'   , 'KeyJ'   , 'KeyK'   , 'KeyL'   , 'Semicolon' , 'Quote'       ,
  'KeyZ'   , 'KeyX'   , 'KeyC'   , 'KeyV'   , 'KeyB'   , 'KeyN'   , 'KeyM'   , 'Comma'  , 'Period' , 'Slash'     ,

              'NumpadDivide' , 'NumpadMultiply' , 'NumpadSubtract' ,
  'Numpad7' , 'Numpad8'      , 'Numpad9'        , 'NumpadAdd'      ,
  'Numpad4' , 'Numpad5'      , 'Numpad6'        ,
  'Numpad1' , 'Numpad2'      , 'Numpad3'        ,
  'Numpad0'                  , 'NumpadDecimal'  ,

  'NumpadComma'
]);
